// Rare idle Easter eggs: one-shot clips a full-body idle avatar now and then
// plays in place of its idle loop, then goes back to idle
// (hooks/useAvatarIdleVariant.js decides when). Pure data module: no React,
// no DOM. `ms` is one pass of the clip, decoded WebP timing.
//
// Adding a character: import its NAME-egg1 / NAME-egg2 clips below and list
// them under its emoji. An avatar with no entry simply never plays an egg.
import dancerWomanEgg1 from '../assets/avatar-motion/dancer-woman/dancer-woman-egg1.webp';
import dancerWomanEgg2 from '../assets/avatar-motion/dancer-woman/dancer-woman-egg2.webp';
import dancerWomanEgg3 from '../assets/avatar-motion/dancer-woman/dancer-woman-egg3.webp';
import dancerManEgg1 from '../assets/avatar-motion/dancer-man/dancer-man-egg1.webp';
import dancerManEgg2 from '../assets/avatar-motion/dancer-man/dancer-man-egg2.webp';
import dragonEgg1 from '../assets/avatar-motion/dragon/dragon-egg1.webp';
import dragonEgg2 from '../assets/avatar-motion/dragon/dragon-egg2.webp';
import dinoEgg1 from '../assets/avatar-motion/dino/dino-egg1.webp';
import dinoEgg2 from '../assets/avatar-motion/dino/dino-egg2.webp';
import ninjaEgg1 from '../assets/avatar-motion/ninja/ninja-egg1.webp';
import ninjaEgg2 from '../assets/avatar-motion/ninja/ninja-egg2.webp';
import unicornEgg1 from '../assets/avatar-motion/unicorn/unicorn-egg1.webp';
import unicornEgg2 from '../assets/avatar-motion/unicorn/unicorn-egg2.webp';
import octopusEgg1 from '../assets/avatar-motion/octopus/octopus-egg1.webp';
import octopusEgg2 from '../assets/avatar-motion/octopus/octopus-egg2.webp';
import octopusEgg3 from '../assets/avatar-motion/octopus/octopus-egg3.webp';
import skeletonEgg1 from '../assets/avatar-motion/skeleton/skeleton-egg1.webp';
import teddyEgg2 from '../assets/avatar-motion/teddy/teddy-egg2.webp';
import alienEgg1 from '../assets/avatar-motion/alien/alien-egg1.webp';
import teddyEgg1 from '../assets/avatar-motion/teddy/teddy-egg1.webp';
import heartEgg1 from '../assets/avatar-motion/pink-heart/pink-heart-egg1.webp';
import grapesEgg1 from '../assets/avatar-motion/grapes/grapes-egg1.webp';
import crownEgg1 from '../assets/avatar-motion/crown/crown-egg1.webp';

import { getSmallClipSrc } from './avatarMotionSmall';
import { FINAL_THREE_EGGS } from './avatarMotionFinalThree';

const variants = (...list) => Object.freeze(list.map(([id, src, ms, placement]) => Object.freeze({ id, src, ms, placement })));
const padded = Object.freeze({ scale: 1.5, x: -25, y: -25 });
const unicornPadded = Object.freeze({ scale: 1.5, x: -15.104166666666629, y: -25 });

const REGISTRY = {
  ...FINAL_THREE_EGGS,
  '🦖': variants(['egg1', dinoEgg1, 4042, padded], ['egg2', dinoEgg2, 4042, padded]),
  '🥷': variants(['egg1', ninjaEgg1, 4042, padded], ['egg2', ninjaEgg2, 4042, padded]),
  '🦄': variants(['egg1', unicornEgg1, 4042, unicornPadded], ['egg2', unicornEgg2, 4042, unicornPadded]),
  '🐙': variants(['egg1', octopusEgg1, 4042, padded], ['egg2', octopusEgg2, 4042, padded], ['egg3', octopusEgg3, 4042, padded]),
  '💃': variants(['egg1', dancerWomanEgg1, 2310], ['egg2', dancerWomanEgg2, 1638], ['egg3', dancerWomanEgg3, 4042, padded]),
  '🕺': variants(['egg1', dancerManEgg1, 1848], ['egg2', dancerManEgg2, 1344]),
  '🐲': variants(['egg1', dragonEgg1, 2520], ['egg2', dragonEgg2, 2814]),
  '💀': variants(['egg1', skeletonEgg1, 4042, padded]),
  '👽': variants(['egg1', alienEgg1, 4042]),
  '🧸': variants(['egg1', teddyEgg1, 4042, padded], ['egg2', teddyEgg2, 4042]),
  '🩷': variants(['egg1', heartEgg1, 4042, padded]),
  '🍇': variants(['egg1', grapesEgg1, 4042, padded]),
  '👑': variants(['egg1', crownEgg1, 4042]),
};

const NONE = Object.freeze([]);

const smallVariants = new Map();

// -> [{ id, src, ms, placement? }]; empty for an avatar without eggs.
// `small`: the 28 px lobby chips get each egg's small copy as `src` (placement
// is a scale on the chip, so it carries over), or the full egg when there is none.
export function getAvatarIdleVariants(emoji, { small = false } = {}) {
  const full = Object.prototype.hasOwnProperty.call(REGISTRY, emoji) ? REGISTRY[emoji] : NONE;
  if (!small || !full.length) return full;
  if (!smallVariants.has(emoji)) {
    smallVariants.set(emoji, Object.freeze(full.map((v) => {
      const src = getSmallClipSrc(emoji, v.id);
      return src ? Object.freeze({ ...v, src }) : v;
    })));
  }
  return smallVariants.get(emoji);
}

// Expanded canvases leave room for props while preserving the idle body scale.
export function getAvatarIdleVariantStyle(variant) {
  const p = variant?.placement;
  return p ? { transformOrigin: '0 0', transform: `translate(${p.x}%, ${p.y}%) scale(${p.scale})` } : undefined;
}
